import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Play, Pause, Square, AlertCircle, RefreshCw, Mail, Eye, MessageSquare, MousePointer, Clock } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

// ─── ETA CARD ─────────────────────────────────────────────────────────────────

function ETACard({ totalLeads, sent, delaySeconds, batchLimit, finishTime }) {
  const [secondsLeft, setSecondsLeft] = useState(0);

  // Calculate seconds left based on finish time
  useEffect(() => {
    if (!finishTime) return;
    const tick = () => {
      const diff = Math.max(0, Math.floor((finishTime - Date.now()) / 1000));
      setSecondsLeft(diff);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [finishTime]);

  const remaining  = Math.max(totalLeads - sent, 0);
  const progress   = totalLeads > 0 ? Math.min(Math.round((sent / totalLeads) * 100), 100) : 0;

  const hours   = Math.floor(secondsLeft / 3600);
  const minutes = Math.floor((secondsLeft % 3600) / 60);
  const secs    = secondsLeft % 60;

  let timeStr = '';
  if (hours > 0)   timeStr += `${hours}h `;
  if (minutes > 0) timeStr += `${minutes}m `;
  timeStr += `${secs}s`;

  const finishStr = finishTime
    ? finishTime.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderLeft: '3px solid var(--blue)',
      borderRadius: 'var(--radius)', padding: '16px 20px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Clock size={14} color="var(--blue)" />
        <span style={{ fontSize: 13, fontWeight: 500 }}>Campaign progress</span>
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>
          <span>{sent.toLocaleString()} sent</span>
          <span>{progress}%</span>
          <span style={{ color: 'var(--text3)' }}>{totalLeads.toLocaleString()} total</span>
        </div>
        <div style={{ height: 6, background: 'var(--bg3)', borderRadius: 20, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 20, background: 'var(--blue)',
            width: `${progress}%`, transition: 'width 0.5s ease',
          }} />
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)' }}>{remaining.toLocaleString()}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>leads remaining</div>
        </div>
        <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--amber)' }}>{timeStr}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>time remaining</div>
        </div>
        <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--green)' }}>{finishStr}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>estimated finish</div>
        </div>
      </div>

      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 10, textAlign: 'center' }}>
        Sending 1 email every {delaySeconds}s · auto-switches accounts every {batchLimit} sends
      </div>
    </div>
  );
}

// ─── METRIC CARD ──────────────────────────────────────────────────────────────

function Metric({ label, value, sub, color }) {
  return (
    <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '14px 18px' }}>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: color || 'var(--text)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ─── LIVE LOG ─────────────────────────────────────────────────────────────────

function LiveLog({ log }) {
  const iconMap = {
    sent:    { icon: <Mail size={12} />,         color: 'var(--blue)',  label: 'Sent' },
    opened:  { icon: <Eye size={12} />,          color: 'var(--green)', label: 'Opened' },
    clicked: { icon: <MousePointer size={12} />, color: 'var(--blue)',  label: 'Clicked' },
    replied: { icon: <MessageSquare size={12} />,color: 'var(--amber)', label: 'Replied' },
    bounced: { icon: <AlertCircle size={12} />,  color: 'var(--red)',   label: 'Bounced' },
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 500 }}>
        Live activity
      </div>
      {!log || log.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>
          No activity yet — launch the campaign to start sending
        </div>
      ) : (
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          {log.map((entry, i) => {
            const s = iconMap[entry.status] || iconMap.sent;
            const name = [entry.first_name, entry.last_name].filter(Boolean).join(' ') || entry.to_email;
            const time = new Date(entry.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 20px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                <span style={{ color: s.color, display: 'flex', alignItems: 'center' }}>{s.icon}</span>
                <span style={{ flex: 1, color: 'var(--text)' }}>
                  <strong>{s.label}</strong> → {name}
                  <span style={{ color: 'var(--text3)', marginLeft: 6 }}>{entry.to_email}</span>
                </span>
                <span style={{ color: 'var(--text3)', fontSize: 11, flexShrink: 0 }}>{entry.from_account}</span>
                <span style={{ color: 'var(--text3)', fontSize: 11, width: 44, textAlign: 'right' }}>{time}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function CampaignDetail() {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [showTestBox, setShowTestBox] = useState(false);

  // finishTime is calculated ONCE on first load and never changes
  const finishTimeRef = useRef(null);
  const [finishTime, setFinishTime] = useState(null);
  const totalLeadsRef = useRef(null); // original total, set once

  const load = useCallback(async (firstLoad = false) => {
    try {
      const [cRes, aRes, lRes] = await Promise.all([
        api.get(`/api/campaigns/${id}`),
        api.get(`/api/campaigns/${id}/analytics`),
        api.get(`/api/campaigns/${id}/leads`),
      ]);
      const c = cRes.data.campaign;
      const a = aRes.data;

      setCampaign(c);
      setAnalytics(a);
      setLeads(lRes.data.leads || []);

      // On first load of a running campaign — set finish time ONCE
      if (firstLoad && c.status === 'running' && !finishTimeRef.current) {
        const sent      = parseInt(a.totals?.sent || 0);
        // Total = all leads in the list (active + any status)
        const total     = lRes.data.leads?.length || (parseInt(a.sequence?.active || 0) + sent);
        const remaining = Math.max(total - sent, 0);
        const secs      = remaining * parseInt(c.delay_seconds || 10);
        const ft        = new Date(Date.now() + secs * 1000);
        finishTimeRef.current = ft;
        totalLeadsRef.current = total;
        setFinishTime(ft);
      }
    } catch { toast.error('Failed to load campaign'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(true); }, [load]);

  // Auto-refresh every 10s while running
  useEffect(() => {
    if (!campaign || campaign.status !== 'running') return;
    const interval = setInterval(() => load(false), 10000);
    return () => clearInterval(interval);
  }, [campaign, load]);

  const action = async (act) => {
    try {
      await api.post(`/api/campaigns/${id}/${act}`);
      toast.success(act === 'launch' ? 'Campaign launched!' : `Campaign ${act}ed`);
      // Reset finish time if relaunching
      if (act === 'launch' || act === 'resume') {
        finishTimeRef.current = null;
        totalLeadsRef.current = null;
        setFinishTime(null);
      }
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${act}`);
    }
  };

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes('@')) return toast.error('Enter a valid test email address');
    setSendingTest(true);
    try {
      const res = await api.post(`/api/campaigns/${id}/test`, { testEmail });
      toast.success(`✅ Test sent to ${res.data.sentTo} from ${res.data.sentFrom}`);
      setShowTestBox(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send test');
    } finally { setSendingTest(false); }
  };

  const statusBadge = (s) => {
    const map = { running: 'badge-blue', paused: 'badge-amber', completed: 'badge-green', draft: 'badge-gray', stopped: 'badge-red' };
    return <span className={`badge ${map[s] || 'badge-gray'}`}>{s}</span>;
  };

  const leadBadge = (s) => {
    const map = { active: 'badge-blue', replied: 'badge-green', cold: 'badge-gray', unsubscribed: 'badge-amber', bounced: 'badge-red' };
    return <span className={`badge ${map[s] || 'badge-gray'}`}>{s}</span>;
  };

  if (loading) return <div style={{ color: 'var(--text3)', fontSize: 13, marginTop: 60, textAlign: 'center' }}>Loading…</div>;
  if (!campaign) return <div style={{ color: 'var(--red)', fontSize: 13 }}>Campaign not found</div>;

  const { totals = {}, rates = {}, recentLog = [] } = analytics || {};
  const sent      = parseInt(totals.sent || 0);
  // Use stable total from ref, or calculate from leads length
  const totalLeads = totalLeadsRef.current || leads.length || sent;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <h1 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.4px' }}>{campaign.name}</h1>
            {statusBadge(campaign.status)}
          </div>
          <p style={{ color: 'var(--text2)', fontSize: 13 }}>{campaign.list_name}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => load(false)} title="Refresh"><RefreshCw size={13} /></button>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowTestBox(!showTestBox)} style={{ color: 'var(--amber)', borderColor: 'var(--amber)' }}>
            <Mail size={13} /> Send test
          </button>
          {campaign.status === 'draft' && (
            <button className="btn btn-primary btn-sm" onClick={() => action('launch')}>
              <Play size={13} /> Launch
            </button>
          )}
          {campaign.status === 'running' && (
            <button className="btn btn-ghost btn-sm" onClick={() => action('pause')}>
              <Pause size={13} /> Pause
            </button>
          )}
          {campaign.status === 'paused' && (
            <button className="btn btn-primary btn-sm" onClick={() => action('resume')}>
              <Play size={13} /> Resume
            </button>
          )}
          {['running', 'paused'].includes(campaign.status) && (
            <button className="btn btn-danger btn-sm" onClick={() => action('stop')}>
              <Square size={13} /> Stop
            </button>
          )}
        </div>
      </div>

      {/* Test email box */}
      {showTestBox && (
        <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius)', padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Mail size={16} color="var(--amber)" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Send test email</div>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>Sends a sample of your first email step to verify it looks correct before launching</div>
          </div>
          <input
            className="input"
            type="email"
            placeholder="youremail@gmail.com"
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            style={{ width: 240 }}
            onKeyDown={e => e.key === 'Enter' && sendTest()}
          />
          <button className="btn btn-primary btn-sm" onClick={sendTest} disabled={sendingTest}>
            {sendingTest ? 'Sending…' : 'Send test'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowTestBox(false)}>✕</button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        {['overview', 'leads'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: 'none', border: 'none',
            color: tab === t ? 'var(--text)' : 'var(--text2)',
            fontSize: 13, fontWeight: tab === t ? 500 : 400,
            padding: '8px 14px',
            borderBottom: tab === t ? '2px solid var(--blue)' : '2px solid transparent',
            marginBottom: -1, cursor: 'pointer', textTransform: 'capitalize',
          }}>{t}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
          {/* ETA Card — only when running */}
          {campaign.status === 'running' && finishTime && (
            <ETACard
              totalLeads={totalLeads}
              sent={sent}
              delaySeconds={campaign.delay_seconds}
              batchLimit={campaign.batch_limit}
              finishTime={finishTime}
            />
          )}

          {/* Metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
            <Metric label="Sent"         value={sent.toLocaleString()} />
            <Metric label="Bounced"      value={totals.bounced || 0} sub={`${rates.bounce || 0}% bounce rate`} color={parseFloat(rates.bounce) > 3 ? 'var(--red)' : 'var(--text)'} />
            <Metric label="In sequence"  value={analytics?.sequence?.active || 0} sub="leads active" />
          </div>

          <div className="card" style={{ padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Settings</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, fontSize: 12 }}>
              <div><span style={{ color: 'var(--text3)' }}>Batch limit:</span> <strong>{campaign.batch_limit} per account</strong></div>
              <div><span style={{ color: 'var(--text3)' }}>Email delay:</span> <strong>{campaign.delay_seconds}s</strong></div>
              <div><span style={{ color: 'var(--text3)' }}>Track opens:</span> <strong>{campaign.track_opens ? 'Yes' : 'No'}</strong></div>
            </div>
          </div>

          <LiveLog log={recentLog} />
        </>
      )}

      {tab === 'leads' && (
        <div className="card">
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 500 }}>
            {leads.length} leads in sequence
          </div>
          {leads.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No leads yet</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Contact', 'Status', 'Step', 'Next send', 'Replied'].map(h => (
                    <th key={h} style={{ padding: '9px 20px', textAlign: 'left', fontSize: 11, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map((l, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 20px' }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{[l.first_name, l.last_name].filter(Boolean).join(' ') || l.email}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{l.email}</div>
                    </td>
                    <td style={{ padding: '10px 20px' }}>{leadBadge(l.status)}</td>
                    <td style={{ padding: '10px 20px', fontSize: 12, color: 'var(--text2)' }}>Step {l.current_step + 1}</td>
                    <td style={{ padding: '10px 20px', fontSize: 12, color: 'var(--text2)' }}>
                      {l.next_send_at ? new Date(l.next_send_at).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '10px 20px', fontSize: 12, color: l.replied_at ? 'var(--green)' : 'var(--text3)' }}>
                      {l.replied_at ? new Date(l.replied_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
